import axios from 'axios';
import { supabase } from './supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Flag para evitar múltiples refreshes simultáneos
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Función para refrescar el token directamente usando el SDK de Supabase JS
// Esto evita condiciones de carrera globales en el backend de Python
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const { data, error } = await supabase.auth.refreshSession({ refresh_token: refreshToken });
  
  if (error) {
    throw error;
  }
  
  if (!data.session) {
    throw new Error('No session returned');
  }

  const { access_token, refresh_token: newRefreshToken } = data.session;
  localStorage.setItem('access_token', access_token);
  localStorage.setItem('refresh_token', newRefreshToken);

  return access_token;
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Organización inactiva — limpiar sesión y redirigir con mensaje
    if (error.response?.status === 403 && error.response?.data?.detail === 'ORGANIZACION_INACTIVA') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('selected_proyecto');
      // Evitar redirecciones en bucle si ya estamos en /login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?suspended=1';
      }
      return Promise.reject(error);
    }

    // Si es error 401 y no es un retry y no es el endpoint de refresh
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {

      if (isRefreshing) {
        // Si ya está refrescando, encolar la petición
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const oldRefreshToken = localStorage.getItem('refresh_token');

      try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Multi-tab check: Did another tab successfully refresh the token?
        const currentRefreshToken = localStorage.getItem('refresh_token');
        const currentAccessToken = localStorage.getItem('access_token');
        
        if (currentRefreshToken && currentRefreshToken !== oldRefreshToken) {
          // Another tab refreshed it successfully! Recover gracefully.
          processQueue(null, currentAccessToken);
          originalRequest.headers.Authorization = `Bearer ${currentAccessToken}`;
          return api(originalRequest);
        }

        processQueue(refreshError, null);
        // Si el refresh falla y nadie más lo ha refrescado, hacer logout
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('selected_proyecto');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const signup = (email, password) => api.post('/auth/signup', { email, password });
export const logout = () => api.post('/auth/logout');
export const refreshToken = (refresh_token) => api.post('/auth/refresh', { refresh_token });
export const getSessionData = (orgId = null) => api.get('/auth/session-data', { params: orgId ? { org_id: orgId } : {} });
export const getMisOrganizaciones = () => api.get('/auth/mis-organizaciones');

// Password Reset
export const requestPasswordReset = (email) => api.post('/auth/password-reset-request', { email });
export const verifyResetToken = (token) => api.get(`/auth/verify-reset-token/${token}`);
export const resetPassword = (token, new_password) => api.post('/auth/password-reset', { token, new_password });
export const adminResetPassword = (user_email, new_password) => api.post('/admin/usuarios/reset-password', { user_email, new_password });

// Admin Check
export const checkAdmin = () => api.get('/admin/check');

// Organizaciones
export const getOrganizaciones = () => api.get('/organizaciones');
export const getMiOrganizacion = () => api.get('/organizaciones/me');
export const invitarOrganizacion = (data) => api.post('/organizaciones/invitar', data);
export const updateOrganizacion = (orgId, data) => api.put(`/organizaciones/${orgId}`, data);
export const deleteOrganizacion = (orgId) => api.delete(`/organizaciones/${orgId}`);
export const getProyectosOrganizacion = (orgId) => api.get(`/organizaciones/${orgId}/proyectos`);

// Platform Config (Platform Admin)
export const getPlatformConfig = () => api.get('/platform/config');
export const updatePlatformConfig = (data) => api.put('/platform/config', data);

// Super Admins
export const getSuperAdmins = (orgId) => api.get(`/organizaciones/${orgId}/super-admins`);
export const addSuperAdmin = (orgId, data) => api.post(`/organizaciones/${orgId}/super-admins`, data);
export const removeSuperAdmin = (orgId, superAdminId) => api.delete(`/organizaciones/${orgId}/super-admins/${superAdminId}`);

// Proyectos
export const getProyectos = () => api.get('/proyectos');
export const getProyecto = (proyectoId) => api.get(`/proyectos/${proyectoId}`);
export const getProyectoRol = (proyectoId) => api.get(`/proyectos/${proyectoId}/rol`);

// Dashboard
export const getDashboardAll = (proyectoId, params = {}) =>
  api.get('/dashboard/all', { params: { proyecto_id: proyectoId, ...params } });
export const getDashboardMetrics = (proyectoId, params = {}) =>
  api.get('/dashboard/metrics', { params: { proyecto_id: proyectoId, ...params } });
export const getRankingClosers = (proyectoId, params = {}) =>
  api.get('/dashboard/ranking-closers', { params: { proyecto_id: proyectoId, ...params } });
export const getRankingSetters = (proyectoId, params = {}) =>
  api.get('/dashboard/ranking-setters', { params: { proyecto_id: proyectoId, ...params } });
export const getRankingLideres = (proyectoId, params = {}) =>
  api.get('/dashboard/ranking-lideres', { params: { proyecto_id: proyectoId, ...params } });
export const getUltimasTransacciones = (proyectoId, limit = 10) =>
  api.get('/dashboard/ultimas-transacciones', { params: { proyecto_id: proyectoId, limit } });
export const getIngresosPorDia = (proyectoId, params = {}) =>
  api.get('/dashboard/ingresos-por-dia', { params: { proyecto_id: proyectoId, ...params } });

// Transactions
export const getTransacciones = (proyectoId, params = {}) =>
  api.get('/transacciones', { params: { proyecto_id: proyectoId, ...params } });
export const getTransaccion = (id) => api.get(`/transacciones/${id}`);
export const createTransaccion = (data) => api.post('/transacciones', data);
export const updateTransaccion = (id, data, proyectoId) =>
  api.put(`/transacciones/${id}`, data, { params: { proyecto_id: proyectoId } });
export const deleteTransaccion = (id, proyectoId) =>
  api.delete(`/transacciones/${id}`, { params: { proyecto_id: proyectoId } });
export const exportTransaccionesConUTM = (proyectoId, params = {}) =>
  api.get('/transacciones/export', { params: { proyecto_id: proyectoId, ...params } });
export const uploadComprobante = (file, proyectoId) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/transacciones/upload-comprobante', formData, {
    params: { proyecto_id: proyectoId },
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Metas
export const getMetas = (proyectoId) => api.get('/metas', { params: { proyecto_id: proyectoId } });

// Filters
export const getClosers = (proyectoId) => api.get('/filters/closers', { params: { proyecto_id: proyectoId } });
export const getSetters = (proyectoId) => api.get('/filters/setters', { params: { proyecto_id: proyectoId } });
export const getTipos = (proyectoId) => api.get('/filters/tipos', { params: { proyecto_id: proyectoId } });

// Equipo
export const getEquipoProyecto = (proyectoId) => api.get(`/proyectos/${proyectoId}/equipo`);

// Admin
export const adminGetProyectos = () => api.get('/admin/proyectos');
export const adminCreateProyecto = (data) => api.post('/admin/proyectos', data);
export const adminUpdateProyecto = (id, data) => api.put(`/admin/proyectos/${id}`, data);
export const adminDeleteProyecto = (id) => api.delete(`/admin/proyectos/${id}`);
export const adminRegenerarWebhookSecret = (proyectoId) => api.post(`/admin/proyectos/${proyectoId}/regenerar-webhook-secret`);
export const adminGenerarWebhookSecretsFaltantes = () => api.post('/admin/proyectos/generar-webhook-secrets-faltantes');
export const adminGetUsuarios = () => api.get('/admin/usuarios');
export const adminCreateUsuario = (data) => api.post('/admin/usuarios/crear', data);
export const adminResendInvite = (email, nombre = '') => api.post('/admin/usuarios/resend-invite', { email, nombre });
export const adminGetProyectoUsuarios = (proyectoId) => api.get(`/admin/proyectos/${proyectoId}/usuarios`);
export const adminAddUsuarioProyecto = (proyectoId, data) => api.post(`/admin/proyectos/${proyectoId}/usuarios`, data);
export const adminUpdateUsuarioProyecto = (asignacionId, data) => api.put(`/admin/usuarios-proyectos/${asignacionId}`, data);
export const adminDeleteUsuarioProyecto = (asignacionId) => api.delete(`/admin/usuarios-proyectos/${asignacionId}`);
export const adminGetMetas = (proyectoId) => api.get(`/admin/metas/${proyectoId}`);
export const adminCreateOrUpdateMetas = (proyectoId, metas) => api.post(`/admin/metas/${proyectoId}/bulk`, { metas });


// Admin - Equipo
export const adminGetEquipo = (proyectoId) => api.get(`/admin/proyectos/${proyectoId}/equipo`);
export const adminCreateMiembro = (data) => api.post('/admin/equipo', data);
export const adminCreateMiembroConCuenta = (data) => api.post('/admin/equipo/con-cuenta', data);
export const adminCrearCuentaMiembro = (miembroId, data) => api.post(`/admin/equipo/${miembroId}/crear-cuenta`, data);
export const adminUpdateMiembro = (miembroId, data) => api.put(`/admin/equipo/${miembroId}`, data);
export const adminDeleteMiembro = (miembroId) => api.delete(`/admin/equipo/${miembroId}`);

// Liquidaciones
export const getLiquidacionMiembro = (miembroId, proyectoId, fechaInicio, fechaFin) =>
  api.get(`/liquidaciones/miembro/${miembroId}`, {
    params: { proyecto_id: proyectoId, fecha_inicio: fechaInicio, fecha_fin: fechaFin }
  });

// Socios
export const getSocios = (proyectoId) => api.get('/socios', { params: { proyecto_id: proyectoId } });
export const createSocio = (data) => api.post('/socios', data);
export const updateSocio = (socioId, data, proyectoId) =>
  api.put(`/socios/${socioId}`, data, { params: { proyecto_id: proyectoId } });
export const deleteSocio = (socioId, proyectoId) =>
  api.delete(`/socios/${socioId}`, { params: { proyecto_id: proyectoId } });

// Deducciones
export const getDeducciones = (proyectoId) => api.get('/deducciones', { params: { proyecto_id: proyectoId } });
export const createDeduccion = (data) => api.post('/deducciones', data);
export const updateDeduccion = (deduccionId, data, proyectoId) =>
  api.put(`/deducciones/${deduccionId}`, data, { params: { proyecto_id: proyectoId } });
export const deleteDeduccion = (deduccionId, proyectoId) =>
  api.delete(`/deducciones/${deduccionId}`, { params: { proyecto_id: proyectoId } });

// Medios de Pago
export const getMediosPago = (proyectoId) => api.get('/medios-pago', { params: { proyecto_id: proyectoId } });
export const createMedioPago = (data) => api.post('/medios-pago', data);
export const updateMedioPago = (medioId, data, proyectoId) =>
  api.put(`/medios-pago/${medioId}`, data, { params: { proyecto_id: proyectoId } });
export const deleteMedioPago = (medioId, proyectoId) =>
  api.delete(`/medios-pago/${medioId}`, { params: { proyecto_id: proyectoId } });

// Productos del Proyecto
export const getProductosProyecto = (proyectoId) => api.get('/productos-proyecto', { params: { proyecto_id: proyectoId } });
export const getAllProductosProyecto = (proyectoId) => api.get('/productos-proyecto/all', { params: { proyecto_id: proyectoId } });
export const createProductoProyecto = (data) => api.post('/productos-proyecto', data);
export const updateProductoProyecto = (productoId, data, proyectoId) =>
  api.put(`/productos-proyecto/${productoId}`, data, { params: { proyecto_id: proyectoId } });
export const deleteProductoProyecto = (productoId, proyectoId) =>
  api.delete(`/productos-proyecto/${productoId}`, { params: { proyecto_id: proyectoId } });

// Traffic Metrics
export const getTrafficVisits = (proyectoId, params = {}) =>
  api.get('/traffic/visits', { params: { proyecto_id: proyectoId, ...params } });

export const getSalesWithTracking = (proyectoId, params = {}) =>
  api.get('/traffic/sales-with-tracking', { params: { proyecto_id: proyectoId, ...params } });
export const getTrafficStats = (proyectoId) => api.get(`/traffic/stats/${proyectoId}`);
export const getTrafficAnalytics = (proyectoId, grupoPor = 'utm_source', fechaInicio = null, fechaFin = null, campaignId = null) =>
  api.get('/traffic/analytics', {
    params: {
      proyecto_id: proyectoId,
      grupo_por: grupoPor,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      ...(campaignId ? { traffic_campaign_id: campaignId } : {})
    }
  });

// Traffic Metrics — Campañas Internas
export const getTrafficCampaigns = (proyectoId, includeInactive = false) =>
  api.get('/traffic/campaigns', { params: { proyecto_id: proyectoId, include_inactive: includeInactive } });
export const createTrafficCampaign = (data) => api.post('/traffic/campaigns', data);
export const updateTrafficCampaign = (campaignId, data) => api.put(`/traffic/campaigns/${campaignId}`, data);
export const deleteTrafficCampaign = (campaignId) => api.delete(`/traffic/campaigns/${campaignId}`);
export const getTrafficCampaignStats = (campaignId, params = {}) =>
  api.get(`/traffic/campaigns/${campaignId}/stats`, { params });
export const linkAdsCampaign = (campaignId, data) => api.post(`/traffic/campaigns/${campaignId}/ads-link`, data);
export const unlinkAdsCampaign = (campaignId, linkId) => api.delete(`/traffic/campaigns/${campaignId}/ads-link/${linkId}`);

export const getTrafficLeads = (proyectoId, params = {}) =>
  api.get('/traffic/leads', { params: { proyecto_id: proyectoId, ...params } });

// Dominios Tracking
export const getProyectoDominios = (proyectoId) => api.get(`/admin/proyectos/${proyectoId}/dominios`);
export const updateProyectoDominios = (proyectoId, data) => api.put(`/admin/proyectos/${proyectoId}/dominios`, data);
export const getProyectoLogsBloqueados = (proyectoId, limit = 50) =>
  api.get(`/admin/proyectos/${proyectoId}/logs-bloqueados`, { params: { limit } });

// Cartera - Verificar vencimientos
export const verificarVencimientosCartera = (proyectoId) =>
  api.post('/cartera/verificar-vencimientos', null, { params: { proyecto_id: proyectoId } });

// Notificaciones
export const getNotificaciones = (limit = 50, soloNoLeidas = false) =>
  api.get('/notificaciones', { params: { limit, solo_no_leidas: soloNoLeidas } });
export const getNotificacionesCount = () => api.get('/notificaciones/count');
export const marcarNotificacionLeida = (notificacionId) =>
  api.patch(`/notificaciones/${notificacionId}/leer`);
export const marcarTodasLeidas = () => api.patch('/notificaciones/leer-todas');
export const crearNotificacion = (data) => api.post('/notificaciones', data);
export const getPreferenciasNotificaciones = () => api.get('/notificaciones/preferencias');
export const updatePreferenciasNotificaciones = (data) => api.put('/notificaciones/preferencias', data);
export const getHistorialNotificaciones = (limit = 50) =>
  api.get('/notificaciones/historial', { params: { limit } });

// Informes Socios
export const calcularInformeSocios = (proyectoId, fechaInicio, fechaFin) =>
  api.get('/informes-socios/calcular', {
    params: { proyecto_id: proyectoId, fecha_inicio: fechaInicio, fecha_fin: fechaFin }
  });
export const getInformesSocios = (proyectoId) =>
  api.get('/informes-socios', { params: { proyecto_id: proyectoId } });
export const guardarInformeSocios = (data) => api.post('/informes-socios', data);
export const deleteInformeSocios = (informeId, proyectoId) =>
  api.delete(`/informes-socios/${informeId}`, { params: { proyecto_id: proyectoId } });

// Cartera APIs
export const getCarteraResumen = (proyectoId) =>
  api.get('/cartera/resumen', { params: { proyecto_id: proyectoId } });
export const getPlanesCartera = (proyectoId, estado = null) =>
  api.get('/cartera/planes', { params: { proyecto_id: proyectoId, estado } });
export const getPlanCartera = (planId) => api.get(`/cartera/planes/${planId}`);
export const createPlanCartera = (data) => api.post('/cartera/planes', data);
export const updatePlanCartera = (planId, data) => api.put(`/cartera/planes/${planId}`, data);
export const deletePlanCartera = (planId, proyectoId) =>
  api.delete(`/cartera/planes/${planId}`, { params: { proyecto_id: proyectoId } });
export const updateCuota = (cuotaId, data) => api.put(`/cartera/cuotas/${cuotaId}`, data);
export const updateCuotasPlan = (planId, cuotas) => api.put(`/cartera/planes/${planId}/cuotas`, { cuotas });
export const pagarCuota = (cuotaId, proyectoId, medioPago, otorgarAcceso = false) =>
  api.post(`/cartera/cuotas/${cuotaId}/pagar`, { proyecto_id: proyectoId, medio_pago: medioPago, otorgar_acceso: otorgarAcceso });

// Clientes APIs
export const getClientes = (proyectoId) =>
  api.get('/clientes', { params: { proyecto_id: proyectoId } });
export const getCliente = (clienteId) => api.get(`/clientes/${clienteId}`);
export const createCliente = (data) => api.post('/clientes', data);
export const updateCliente = (clienteId, data) => api.put(`/clientes/${clienteId}`, data);
export const toggleAccesoCliente = (clienteId) => api.post(`/clientes/${clienteId}/toggle-acceso`);
export const deleteCliente = (clienteId, proyectoId) =>
  api.delete(`/clientes/${clienteId}`, { params: { proyecto_id: proyectoId } });

// Portal Vendedor APIs
export const getMiPerfil = (proyectoId) =>
  api.get('/portal/mi-perfil', { params: { proyecto_id: proyectoId } });
export const getMisMetricas = (proyectoId, params = {}) =>
  api.get('/portal/mis-metricas', { params: { proyecto_id: proyectoId, ...params } });
export const getMisTransacciones = (proyectoId, params = {}) =>
  api.get('/portal/mis-transacciones', { params: { proyecto_id: proyectoId, ...params } });
export const getMiCartera = (proyectoId) =>
  api.get('/portal/mi-cartera', { params: { proyecto_id: proyectoId } });
export const getMisReservas = (proyectoId) =>
  api.get('/portal/mis-reservas', { params: { proyecto_id: proyectoId } });

// Platform Admin APIs - Transacciones con revisión pendiente
export const getTransaccionesRevision = () => api.get('/admin/transacciones-revision');
export const marcarTransaccionRevisada = (transaccionId, valorCorregido = null, fechaCorregida = null) => {
  const params = {};
  if (valorCorregido !== null) params.valor_corregido = valorCorregido;
  if (fechaCorregida !== null) params.fecha_corregida = fechaCorregida;
  return api.put(`/admin/transacciones/${transaccionId}/marcar-revisada`, null, { params });
};

// Webhook Logs — Transacciones de proyectos
export const getWebhookLogs = (params = {}) => api.get('/admin/webhook-logs', { params });
export const getWebhookLogPayload = (logId) => api.get(`/admin/webhook-logs/${logId}/payload`);
export const replayWebhookLog = (logId) => api.post(`/admin/webhook-logs/${logId}/replay`);

// Webhook Logs — SaaS (suscripciones de clientes Metricash vía Hotmart)
export const getSaasLogs = (params = {}) => api.get('/admin/saas-logs', { params });
export const getSaasLogPayload = (logId) => api.get(`/admin/saas-logs/${logId}/payload`);

// Admin Proyectos (para filtros)
export const getAdminProyectos = () => api.get('/admin/proyectos');

// Reservas APIs
export const getReservas = (proyectoId) => api.get('/reservas', { params: { proyecto_id: proyectoId } });
export const getReservasResumen = (proyectoId) => api.get('/reservas/resumen', { params: { proyecto_id: proyectoId } });
export const createReserva = (data) => api.post('/reservas', data);
export const updateReserva = (reservaId, data, proyectoId) =>
  api.put(`/reservas/${reservaId}`, data, { params: { proyecto_id: proyectoId } });
export const deleteReserva = (reservaId, proyectoId) =>
  api.delete(`/reservas/${reservaId}`, { params: { proyecto_id: proyectoId } });
export const convertirReservaTransaccion = (reservaId, data, proyectoId) =>
  api.post(`/reservas/${reservaId}/convertir-transaccion`, data, { params: { proyecto_id: proyectoId } });
export const convertirReservaCartera = (reservaId, data, proyectoId) =>
  api.post(`/reservas/${reservaId}/convertir-cartera`, data, { params: { proyecto_id: proyectoId } });
export const reembolsarReserva = (reservaId, data, proyectoId) =>
  api.post(`/reservas/${reservaId}/reembolsar`, data, { params: { proyecto_id: proyectoId } });

// ==================== META ADS APIs ====================

// Conexión OAuth
export const getMetaAdsAuthUrl = (proyectoId) =>
  api.get(`/meta-ads/authorize/${proyectoId}`);

export const metaAdsCallback = (code, state) =>
  api.post('/meta-ads/callback', null, { params: { code, state } });

export const connectMetaAds = (proyectoId, state, adAccountId, adAccountName = null) =>
  api.post(`/meta-ads/connect/${proyectoId}`, null, {
    params: { state, ad_account_id: adAccountId, ad_account_name: adAccountName }
  });

export const getMetaAdsStatus = (proyectoId) =>
  api.get(`/meta-ads/status/${proyectoId}`);

// OPTIMIZADO: Estado combinado de todas las plataformas de anuncios en una sola llamada
export const getAdsStatusCombined = (proyectoId, forceRefresh = false) =>
  api.get(`/ads/status/${proyectoId}`, { params: { force_refresh: forceRefresh } });

export const disconnectMetaAds = (proyectoId, adAccountId = null) =>
  api.delete(`/meta-ads/disconnect/${proyectoId}`, {
    params: adAccountId ? { ad_account_id: adAccountId } : {}
  });

// Campañas, Ad Sets, Ads
export const getMetaCampaigns = (proyectoId, statusFilter = null, dateFrom = null, dateTo = null, adAccountId = null) =>
  api.get(`/meta-ads/campaigns/${proyectoId}`, {
    params: { status_filter: statusFilter, date_from: dateFrom, date_to: dateTo, ad_account_id: adAccountId }
  });

export const getMetaAdSets = (proyectoId, campaignId = null, dateFrom = null, dateTo = null, adAccountId = null) =>
  api.get(`/meta-ads/adsets/${proyectoId}`, {
    params: { campaign_id: campaignId, date_from: dateFrom, date_to: dateTo, ad_account_id: adAccountId }
  });

export const getMetaAds = (proyectoId, adsetId = null, dateFrom = null, dateTo = null, adAccountId = null) =>
  api.get(`/meta-ads/ads/${proyectoId}`, {
    params: { adset_id: adsetId, date_from: dateFrom, date_to: dateTo, ad_account_id: adAccountId }
  });

// Modificar Campañas
export const updateMetaCampaignStatus = (proyectoId, campaignId, status) =>
  api.post(`/meta-ads/campaign/${proyectoId}/${campaignId}/status`, { status });

export const updateMetaCampaignBudget = (proyectoId, campaignId, dailyBudget = null, lifetimeBudget = null) =>
  api.post(`/meta-ads/campaign/${proyectoId}/${campaignId}/budget`, {
    daily_budget: dailyBudget,
    lifetime_budget: lifetimeBudget
  });

export const updateMetaAdSetBudget = (proyectoId, adsetId, dailyBudget = null, lifetimeBudget = null) =>
  api.post(`/meta-ads/adset/${proyectoId}/${adsetId}/budget`, {
    daily_budget: dailyBudget,
    lifetime_budget: lifetimeBudget
  });

// Dashboard de Métricas Cruzadas (Unificado: Meta + Google Ads)
// OPTIMIZADO: Incluye caché de 5 minutos. Usa forceRefresh=true para actualizar
export const getAdsDashboard = (proyectoId, dateStart = null, dateEnd = null, forceRefresh = false, campaignIds = null) =>
  api.get(`/ads/dashboard/${proyectoId}`, {
    params: {
      date_start: dateStart,
      date_end: dateEnd,
      force_refresh: forceRefresh,
      ...(campaignIds && campaignIds.length > 0 && { campaign_ids: campaignIds.join(',') })
    }
  });

// OPTIMIZADO: Dashboard rápido (solo datos locales, < 1 segundo)
export const getAdsDashboardFast = (proyectoId, dateStart = null, dateEnd = null) =>
  api.get(`/ads/dashboard-fast/${proyectoId}`, {
    params: { date_start: dateStart, date_end: dateEnd }
  });

// OPTIMIZADO: Datos de Ads (se llama después de dashboard-fast)
export const getAdsDashboardAds = (proyectoId, dateStart = null, dateEnd = null, totalFacturado = 0) =>
  api.get(`/ads/dashboard-ads/${proyectoId}`, {
    params: { date_start: dateStart, date_end: dateEnd, total_facturado: totalFacturado }
  });

// Alias para compatibilidad (deprecated)
export const getMetaAdsDashboard = getAdsDashboard;

// Insights por nivel/objeto específico (campaña, adset, ad)
export const getMetaInsights = (proyectoId, level = 'account', objectId = null, dateStart = null, dateEnd = null, adAccountId = null) =>
  api.get(`/meta-ads/insights/${proyectoId}`, {
    params: {
      level,
      object_id: objectId,
      date_start: dateStart,
      date_end: dateEnd,
      ad_account_id: adAccountId
    }
  });

// ==================== ADS MULTI-PLATFORM API ====================

// Obtiene las plataformas de anuncios soportadas
export const getAdsPlatforms = () =>
  api.get('/ads/platforms');

// Obtiene todas las conexiones de plataformas para un proyecto
export const getAdsConnections = (proyectoId) =>
  api.get(`/ads/connections/${proyectoId}`);

// Obtiene campañas unificadas de todas las plataformas
export const getUnifiedCampaigns = (proyectoId, platform = null) =>
  api.get(`/ads/campaigns/${proyectoId}`, { params: { platform } });

// Dashboard unificado de métricas de anuncios
export const getUnifiedAdsDashboard = (proyectoId, dateFrom = null, dateTo = null) =>
  api.get(`/ads/dashboard/${proyectoId}`, {
    params: { date_from: dateFrom, date_to: dateTo }
  });

// ==================== GOOGLE ADS APIs ====================

// Conexión OAuth
export const getGoogleAdsAuthUrl = (proyectoId) =>
  api.get(`/google-ads/authorize/${proyectoId}`);

export const googleAdsCallback = (code, state) =>
  api.post('/google-ads/callback', null, { params: { code, state } });

export const connectGoogleAds = (proyectoId, state, customerId, customerName = null) =>
  api.post(`/google-ads/connect/${proyectoId}`, null, {
    params: { state, customer_id: customerId, customer_name: customerName }
  });

export const getGoogleAdsStatus = (proyectoId) =>
  api.get(`/google-ads/status/${proyectoId}`);

export const disconnectGoogleAds = (proyectoId) =>
  api.delete(`/google-ads/disconnect/${proyectoId}`);

// Campañas, Ad Groups, Ads
export const getGoogleCampaigns = (proyectoId, statusFilter = null, dateFrom = null, dateTo = null) =>
  api.get(`/google-ads/campaigns/${proyectoId}`, {
    params: { status_filter: statusFilter, date_from: dateFrom, date_to: dateTo }
  });

export const getGoogleAdGroups = (proyectoId, campaignId = null, dateFrom = null, dateTo = null) =>
  api.get(`/google-ads/adgroups/${proyectoId}`, {
    params: { campaign_id: campaignId, date_from: dateFrom, date_to: dateTo }
  });

export const getGoogleAds = (proyectoId, adgroupId = null, dateFrom = null, dateTo = null) =>
  api.get(`/google-ads/ads/${proyectoId}`, {
    params: { adgroup_id: adgroupId, date_from: dateFrom, date_to: dateTo }
  });

// Insights
export const getGoogleInsights = (proyectoId, level = 'account', objectId = null, dateStart = null, dateEnd = null) =>
  api.get(`/google-ads/insights/${proyectoId}`, {
    params: {
      level,
      object_id: objectId,
      date_start: dateStart,
      date_end: dateEnd
    }
  });

// Modificar Campañas
export const updateGoogleCampaignStatus = (proyectoId, campaignId, status) =>
  api.post(`/google-ads/campaign/${proyectoId}/${campaignId}/status`, null, { params: { status } });

// ==================== SUSCRIPCIÓN SAAS ====================
export const getMiSuscripcion = () => api.get('/suscripcion/mi-suscripcion');
export const getAdminSuscripciones = () => api.get('/admin/suscripciones');
export const getAdminPlanesConfig = () => api.get('/admin/planes-config');
export const syncHotmartSuscripciones = () => api.post('/admin/suscripciones/sync-hotmart');
export const activarTrialOrganizacion = (orgId, dias) =>
  api.post(`/admin/suscripciones/${orgId}/activar-trial`, null, { params: { dias } });

// ── Generic Conversions ──────────────────────────────────────
export const getIntegrations = (proyectoId) =>
  api.get('/integrations', { params: { proyecto_id: proyectoId } });

export const createIntegration = (data) =>
  api.post('/integrations', data);

export const updateIntegration = (integrationId, data) =>
  api.put(`/integrations/${integrationId}`, data);

export const deleteIntegration = (integrationId) =>
  api.delete(`/integrations/${integrationId}`);

export const sendTestWebhook = (integrationId, payload) =>
  api.post(`/integrations/${integrationId}/test-webhook`, payload);

export const getIntegrationConversions = (integrationId, params = {}) =>
  api.get(`/integrations/${integrationId}/conversions`, { params });

export const getIntegrationAttributionStats = (integrationId) =>
  api.get(`/integrations/${integrationId}/attribution-stats`);

export const exportIntegrationConversions = async (integrationId, integrationName, estado = null) => {
  const params = estado ? { estado } : {};
  const response = await api.get(`/integrations/${integrationId}/conversions/export`, {
    params,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  const filename = `conversiones_${(integrationName || integrationId).replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const clearIntegrationConversions = (integrationId) =>
  api.delete(`/integrations/${integrationId}/conversions`);


export default api;
